/**
 * 🔐 SISTEMA DE ROLES UNIFICADO MGI
 * Elimina fragmentación entre frontend y backend
 *
 * P13-V7B (R0-BFF-08) · what this table is, and what it no longer is.
 *
 * It describes the seven canonical roles for the ONE consumer it has left — the vendor sidebar's
 * restored menu (`unified-navigation.service.ts`), whose entries are then filtered by the router's own
 * admission rule before anything is drawn. It used to carry two more authorities of its own:
 *   · `dashboardUrl` — a THIRD role→home table, and six of its seven homes (`/dashboard/super-admin`,
 *     `/dashboard/manager`, `/dashboard/supervisor`, …) were paths no route answers. The single
 *     role→home authority is `getRedirectForRole` (`src/router/guards.ts`).
 *   · `routes` + `canAccessRoute` — a second route-admission table with wildcards of its own. Route
 *     admission is the router's `meta.roles` read by `canonicalGuard`.
 * Neither had a live caller; both are removed so nobody can "fix" them back into use next to the real
 * ones. So are the role classifications (`isAdminRole`, `getRolesByType`, …) and the legacy
 * `data_collector` entry, which nothing consulted.
 */

import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'

// 🎯 TIPOS DE PERMISOS
type Permission = string

// 🎯 ROLES UNIFICADOS — descripción de los siete roles canónicos (no decide admisión ni destino)
export const UNIFIED_ROLES = {
  SUPER_ADMIN: {
    backend: 'super_admin',
    frontend: 'super_admin',
    display: 'Super Administrador',
    permissions: [
      'system:admin', 'users:admin', 'groups:admin',
      'incidents:admin', 'analytics:admin', 'reports:admin'
    ] as Permission[],
    priority: 1
  },

  ADMINISTRATION_MANAGER: {
    backend: 'administration_manager',
    frontend: 'administration_manager',
    display: 'Administration Manager',
    permissions: [
      'users:manage:administration', 'assignments:manage',
      'substitutes:manage', 'administrations:view', 'inventory:view:administration'
    ] as Permission[],
    priority: 2
  },

  GROUP_MANAGER: {
    backend: 'group_manager',
    frontend: 'group_manager',
    display: 'Gerente de Grupo',
    permissions: [
      'groups:manage', 'agencies:manage', 'users:manage:scope',
      'incidents:manage', 'reports:view', 'analytics:view', 'events:view'
    ] as Permission[],
    priority: 3
  },

  FIELD_SUPERVISOR: {
    backend: 'field_supervisor',
    frontend: 'field_supervisor',
    display: 'Supervisor de Campo',
    permissions: [
      'incidents:supervise', 'routes:supervise', 'agencies:supervise',
      'reports:create', 'analytics:view:scope', 'events:view'
    ] as Permission[],
    priority: 4
  },

  ROUTE_OPERATOR: {
    backend: 'route_operator',
    frontend: 'route_operator',
    display: 'Operador de Ruta',
    permissions: [
      'routes:operate', 'incidents:report', 'gps:track',
      'evidence:collect', 'mobile:access', 'events:view'
    ] as Permission[],
    priority: 5
  },

  AGENCY_EMPLOYEE: {
    backend: 'agency_employee',
    frontend: 'agency_employee',
    display: 'Empleado de Agencia',
    permissions: [
      'agency:access', 'incidents:report:basic', 'attendance:register'
    ] as Permission[],
    priority: 6
  },

  DEPARTMENT_HEAD: {
    backend: 'department_head',
    frontend: 'department_head',
    display: 'Jefe de Departamento',
    permissions: [
      'department:manage', 'staff:manage', 'projects:assign',
      'analytics:department', 'reports:department'
    ] as Permission[],
    priority: 7
  }
} as const

// 🔧 UTILIDADES DE ROLES
export class UnifiedRoleService {
  /**
   * Obtener configuración de rol unificada
   */
  static getRoleConfig(role: string) {
    // Buscar por backend name
    let roleConfig = Object.values(UNIFIED_ROLES).find(r => r.backend === role)

    // Buscar por frontend name
    if (!roleConfig) {
      roleConfig = Object.values(UNIFIED_ROLES).find(r => r.frontend === role)
    }

    return roleConfig || null
  }

  /**
   * Verificar si el rol declara un permiso de menú. Selecciona entradas del menú restaurado del
   * sidebar; NO admite rutas (eso lo decide el router) ni capacidades (eso lo decide el backend).
   */
  static hasPermission(userRole: string, permission: string): boolean {
    const config = this.getRoleConfig(userRole)
    if (!config) return false

    return (config.permissions as string[]).includes(permission) ||
           (config.permissions as string[]).includes('system:admin') // Super admin tiene todo
  }
}

// 🔐 COMPOSABLE PARA ROLES (Vue)
export function useUnifiedRoles() {
  const authStore = useAuthStore()

  const currentRole = computed(() => authStore.userRole)
  const roleConfig = computed(() =>
    UnifiedRoleService.getRoleConfig(currentRole.value || '')
  )

  const hasPermission = (permission: string) =>
    UnifiedRoleService.hasPermission(currentRole.value || '', permission)

  return {
    currentRole,
    roleConfig,
    hasPermission
  }
}
